/**
 * Single source of truth for all limits and thresholds for the buy-again feature.
 *
 * Used by: server (get-buy-again route), client (buy-again UI hooks).
 * OpenAPI get-buy-again.yaml documents the API default; keep its limit default
 * in sync with BUY_AGAIN_DEFAULT_LIMIT here.
 */

// --- API (server + client when calling API) ---

/** Default number of buy-again suggestions returned when client omits limit. */
export const BUY_AGAIN_DEFAULT_LIMIT = 3;

/** Server-side cap: requests above this are clamped silently. */
export const BUY_AGAIN_MAX_LIMIT = 10;

/** Validation: limit query param must be >= this. 0 is allowed (no suggestions). */
export const BUY_AGAIN_MIN_LIMIT = 0;

// --- Composite score weights (server-side, not exposed in API) ---
// Each signal is normalised 0→1 before weighting — see shared/buy-again-scoring.ts

/** Weight for gap closeness signal: peaks when z ≈ 0 (due now). */
export const BUY_AGAIN_WEIGHT_GAP = 0.5;

/** Weight for volume signal: log-normalised purchase count within the org. */
export const BUY_AGAIN_WEIGHT_VOLUME = 0.25;

/** Weight for stability signal: 1 − min(cv, 1); lower cv ranks higher. */
export const BUY_AGAIN_WEIGHT_STABILITY = 0.25;

// --- Scoring thresholds (server-side, not exposed in API) ---

/**
 * Minimum z-score for a product to appear in buy-again results.
 * z = (gap - avg_interval) / std_interval
 */
export const BUY_AGAIN_MIN_Z_SCORE = -0.5;

/**
 * Maximum z-score for a product to appear in buy-again results.
 * Excludes products far past due — gap closeness already decays, this caps the window.
 */
export const BUY_AGAIN_MAX_Z_SCORE = 2;

/**
 * Products last purchased more than this many days ago are excluded.
 * Prevents surfacing products the org may have stopped buying.
 */
export const BUY_AGAIN_MAX_GAP_DAYS = 100;

/**
 * Maximum coefficient of variation allowed for buy-again candidates.
 * cv = std / avg; higher values mean more irregular purchase cadence.
 * Products at or above this threshold are excluded from suggestions.
 */
export const BUY_AGAIN_MAX_CV = 0.65;

/**
 * Minimum std used when dividing by std_interval_days.
 * Prevents NaN/Infinity for products with only two purchases (std = 0).
 */
export const BUY_AGAIN_STD_FLOOR = 0.001;

// --- Candidate selection ---

/**
 * Softmax temperature for probabilistic candidate selection.
 * Candidates are sampled without replacement, weighted by exp(score / T).
 * Lower T = more decisive (closer to deterministic top-k).
 * T = 0.3 gives strong candidates a dominant but not guaranteed selection probability,
 * adding just enough noise to avoid always surfacing the exact same products.
 */
export const BUY_AGAIN_SELECTION_TEMPERATURE = 0.3;
